/* The product's seam: what a page calls to record a timing or a failure.
 *
 * Both are spans, and there is deliberately no browser-side metric here. A browser cannot write
 * a metric series the collector can aggregate: the Prometheus exporter's accumulator is a
 * single-writer design, so either every visitor's points collide on one series signature, or —
 * once each page load is given its own instance id to stop the collision — each series is
 * short-lived, held flat for `metric_expiration`, and then gone, which makes `increase()` over
 * it zero. A page load that recorded exactly one failure is the shape that matters most, and it
 * is the shape that vanishes. So the browser emits spans, which are the one thing it *can* write
 * without coordinating, and the collector's `spanmetrics` connector — already in the traces
 * pipeline, multi-writer-safe by construction, counting spans rather than accumulating a
 * counter — derives every number from them.
 *
 * This module imports nothing, and that is its whole reason for existing separately. A product
 * module that reports a failed read is reached from its app's entry, so a static import of the
 * bootstrap would put the whole OpenTelemetry SDK in the entry's modulepreload closure and make
 * the first frame wait for it. `platform/console/scripts/chunk-budgets.ts` holds that closure to
 * what it measured, and it may only fall. The SDK stays behind the one dynamic import the Vite
 * plugin injects, which calls `installRecorder` as soon as it resolves.
 *
 * Nothing is lost in between. A failure during the first paint is the failure most worth having,
 * and it happens before any bootstrap can have finished, so calls made before the install are
 * held in a bounded queue and replayed against the real recorder — carrying the moment they
 * happened, not the moment they were replayed.
 */

/** What a span may carry: scalars, and nothing structured. */
type RecordedAttributes = Readonly<Record<string, string | number | boolean | undefined>>;

/** Every timing the product may record, and nothing else.
 *
 * A closed tuple rather than a `string`, because the metric name becomes a span name and
 * therefore a `span_name` label on `traces_span_metrics_*`. One dynamic value there is one
 * permanent time series per value, so the vocabulary is a type error rather than a convention:
 * adding a timing is editing this list. */
export const UI_DURATION_METRICS = [
  "fleet.event_to_paint",
  "label.edit.input_to_paint",
  "label.scrub.input_to_paint",
  "label.stamp.input_to_paint",
] as const;

type UiDurationMetric = (typeof UI_DURATION_METRICS)[number];

/** Whether a failure is this service's fault.
 *
 * `error` marks the span ERROR, which the `spanmetrics` connector folds into the service's error
 * ratio — the door availability SLO, whose fast burn pages `severity: critical` into Slack. `unset` records
 * the failure in full and stays out of that ratio. A door answering 403 to someone who is not
 * entitled is `unset`; a screen that stopped is `error`. */
type FailureStatus = "error" | "unset";

/** The bootstrap's side of the seam. One implementation, installed once. */
export interface Recorder {
  duration(metric: UiDurationMetric, durationMs: number, at: number): void;
  pageReady(route: string, durationMs: number, at: number): void;
  failure(name: string, attributes: RecordedAttributes, status: FailureStatus, at: number): void;
}

/** A leak bound, not a cache policy: a document that never finishes bootstrapping must not grow
 * a queue, and 64 records is far more than the handful a first paint can produce. */
const maxPending = 64;

let recorder: Recorder | undefined;
const pending: ((into: Recorder) => void)[] = [];

/** Called by the bootstrap once the SDK exists. Replays what happened first, in order. */
export function installRecorder(into: Recorder): void {
  recorder = into;
  for (const replay of pending.splice(0, pending.length)) replay(into);
}

function record(action: (into: Recorder) => void): void {
  if (recorder !== undefined) {
    action(recorder);
    return;
  }
  if (pending.length < maxPending) pending.push(action);
}

/** The longest thing that can still be called a paint latency.
 *
 * Every call site measures across one `requestAnimationFrame`, and a browser **suspends rAF in a
 * hidden tab** while events keep arriving — Fleet's telemetry is an `EventSource`, and Label
 * queues a frame per keypress. An operator who leaves a tab for twenty minutes and comes back
 * fires every queued callback on the first frame, each reporting the whole absence as if it were
 * one frame. A minute is far beyond any real paint and far below a tab switch. */
const maxDurationMs = 60_000;
const maxPageReadyDurationMs = 5 * 60_000;

/** Record one timing as the span `ui.<metric>`, spanning the duration that just elapsed.
 *
 * The seam for a number a machine produced: input-to-paint, event-to-paint. `spanmetrics` turns
 * it into `traces_span_metrics_duration_milliseconds_bucket{span_name="ui.<metric>"}`, a true
 * histogram summed across every browser, because the collector is its only writer.
 *
 * Total by construction, and total about what it refuses as much as what it accepts: a
 * non-finite or negative duration, one longer than `maxDurationMs`, one recorded while the
 * document is hidden, and a document where the bootstrap never ran all record nothing. The two
 * suspended-rAF guards are both needed — the visibility check catches the callback that runs
 * while still hidden, the ceiling catches the one that runs just after the tab came back. A
 * dropped sample costs one observation; a kept one turns a p95 into a measure of tab switching. */
export function recordDuration(metric: UiDurationMetric, durationMs: number): void {
  if (!Number.isFinite(durationMs) || durationMs < 0 || durationMs > maxDurationMs) return;
  // `document` is absent in a worker; a missing visibility state reads as visible, because this
  // surface only ever runs in a document and refusing to record would be the worse default.
  if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
  const at = Date.now();
  record((into) => into.duration(metric, durationMs, at));
}

/** Record the first painted meaningful region for one bounded router pattern. */
export function recordPageReady(route: string, durationMs: number): void {
  if (!route.startsWith("/") || route.length > 160) return;
  if (
    !Number.isFinite(durationMs) ||
    durationMs < 0 ||
    durationMs > maxPageReadyDurationMs
  ) return;
  if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
  const at = Date.now();
  record((into) => into.pageReady(route, durationMs, at));
}

/** Record one caught failure as a zero-duration span under `name`.
 *
 * The window's `error` listener sees only what nothing caught; a React boundary, a query cache
 * and a transport all catch before it would. What they caught arrives here, in full: the
 * bootstrap marks every one of these `sampling.priority: 1`, so the collector keeps 100 % of them
 * rather than the 10 % it keeps of everything else. That is what makes a reference a person reads
 * off the screen findable, and what makes a count of these complete.
 *
 * `status` decides whether the failure is also this service's fault; see `FailureStatus`.
 *
 * Attributes are filtered and truncated by the bootstrap. That filter reads keys, never values,
 * so a caller that puts a door's own message on a span is the one deciding what that message may
 * contain. */
export function recordFailure(
  name: string,
  attributes: RecordedAttributes,
  options: { readonly status: FailureStatus },
): void {
  const at = Date.now();
  const span = name.trim() || "browser.failure";
  record((into) => into.failure(span, attributes, options.status, at));
}

export function _resetRecorderForTests(): void {
  recorder = undefined;
  pending.length = 0;
}
